import { UNAVAILABLE_TEXT, describeCode } from "../api/errors";
import type { SyncState } from "../state/sync";
import type { IconName } from "../components/Icon";
import { formatClock } from "./time";
import { isDedicatedMix, mixDescription, mixLabel, mixPauseDescription } from "./mix";

/**
 * 再生まわりの「いまどうなっているか」。一つの文にまとめず、事情ごとに分ける
 * （更新停止中・Bot停止・同期中・空キュー・動画利用不能・読み込み中・連続失敗・中断）。
 * 見ている本人の事情（VC不参加・権限不足）は src/lib/gates.ts。
 */

export type PlayerConditionKind =
  | "connecting"
  | "stale"
  | "reconnecting"
  | "bot_offline"
  | "syncing"
  | "recovering"
  | "interrupted"
  | "auto_stopped"
  | "video_unavailable"
  | "loading"
  | "error"
  | "empty_queue"
  | "idle"
  | "paused"
  | "mix_waiting"
  | "mix_preparing"
  | "mix_active"
  | "mix_draining"
  | "playing";

export interface PlayerCondition {
  kind: PlayerConditionKind;
  tone: "ok" | "info" | "warn" | "danger";
  icon: IconName;
  /** 短い見出し（固定プレイヤーの1行） */
  title: string;
  /** 説明（いま聴くの帯） */
  detail: string;
}

export function playerCondition(s: SyncState): PlayerCondition {
  if (s.conn === "idle" || s.conn === "connecting") {
    return { kind: "connecting", tone: "info", icon: "refresh", title: "再生の状態を確認中…", detail: "" };
  }
  if (s.conn === "stale" || s.conn === "reconnecting") {
    const at = s.lastSyncAt ? `${formatClock(s.lastSyncAt)} の時点の表示です。` : "";
    return {
      kind: s.conn === "reconnecting" ? "reconnecting" : "stale",
      tone: "warn",
      icon: "wifiOff",
      title: "更新停止中",
      detail: `${at}再生位置は進めずに止めています。つながったら、再生の状態とキューを取り直します。`,
    };
  }
  const p = s.playback;
  if (!p) return { kind: "connecting", tone: "info", icon: "refresh", title: "再生の状態を確認中…", detail: "" };
  if (p.syncing) {
    return {
      kind: "syncing",
      tone: "info",
      icon: "refresh",
      title: "BotがDiscordと同期中",
      detail: "再接続して状態を合わせています。終わるまで再生の操作は受け付けません。",
    };
  }
  if (!p.worker_available) {
    return {
      kind: "bot_offline",
      tone: "danger",
      icon: "warn",
      title: "Botが止まっています",
      detail:
        "Botが停止中か、Discordへ再接続中です。再生の操作はできませんが、投稿やライブラリは見られます。止まっていた間の操作が、あとから急に実行されることはありません。",
    };
  }
  const current = p.current;
  if (p.mix && (isDedicatedMix(p) || !current)) {
    const mix = p.mix;
    const paused = mix.pause_reasons.length > 0;
    return {
      kind: `mix_${mix.phase}`,
      tone: mix.phase === "active" && !paused ? "ok" : "info",
      icon: paused ? "pause" : mix.phase === "active" ? "wave" : "refresh",
      title: `DJ Mix：${mixLabel(mix)}${mix.phase === "draining" && paused ? "（一時停止中）" : ""}`,
      detail: [mixDescription(mix), mixPauseDescription(mix), p.error_code ? describeCode(p.error_code) : ""].filter(Boolean).join(" "),
    };
  }
  if (p.state === "recovering") {
    return {
      kind: "recovering", tone: "warn", icon: "refresh", title: "接続を復旧中",
      detail: "接続を再試行しています。復旧後は切断前の状態に戻り、再生中だった曲は続きから自動で再開します。",
    };
  }
  if (p.error_code === "source_timeout") {
    return { kind: "error", tone: "warn", icon: "warn", title: "音源の読み込みが時間切れになりました",
      detail: describeCode(p.error_code) };
  }
  if (p.error_code === "audio_node_unavailable") {
    return { kind: "error", tone: "danger", icon: "warn", title: "音声サーバーに接続できません",
      detail: `${describeCode(p.error_code)} 再生中だった曲とキューは残しています。` };
  }
  if (p.error_code === "source_access_blocked" || p.error_code === "source_rate_limited" || p.error_code === "youtube_auth_invalid") {
    return {
      kind: "error",
      tone: "danger",
      icon: "warn",
      title: p.error_code === "youtube_auth_invalid" ? "YouTube認証の設定を確認してください" : "YouTubeからの取得が制限されています",
      detail: `${describeCode(p.error_code)} 再生待ちの曲は残しています。`,
    };
  }
  if (current && !current.performance.available) {
    return {
      kind: "video_unavailable",
      tone: "danger",
      icon: "warn",
      title: "この動画は再生できません",
      detail: `${UNAVAILABLE_TEXT[current.performance.unavailable_reason ?? "unknown"] ?? UNAVAILABLE_TEXT.unknown}。保存や投稿は残ります。次の曲へ進みます。`,
    };
  }
  switch (p.state) {
    case "loading":
      return {
        kind: "loading",
        tone: "info",
        icon: "refresh",
        title: "読み込み中",
        detail: "元動画を準備しています。流れ始めるまで位置は進みません。",
      };
    case "interrupted":
      return {
        kind: "interrupted",
        tone: "warn",
        icon: "warn",
        title: "中断しています",
        detail:
          "自動復旧できず、再生が止まりました。キューは残っています。再生を押すと再開します（保存された位置は少しずれることがあります）。",
      };
    case "error":
      // 取得の失敗が続くと、ワーカーは自動送りを止めて error にする（backend worker.py）
      return {
        kind: "auto_stopped",
        tone: "danger",
        icon: "warn",
        title: "再生を止めています",
        detail: `${p.error_code ? describeCode(p.error_code) : "再生できませんでした。"} 続けて再生できなかったため、自動で次へ進むのを止めました。キューは残っています。再生を押すと、次の曲からもう一度試します。`,
      };
    case "paused":
      return { kind: "paused", tone: "info", icon: "pause", title: "一時停止中", detail: "" };
    case "playing":
      return { kind: "playing", tone: "ok", icon: "wave", title: "再生中", detail: "" };
    case "idle":
    default: {
      const waiting = s.queue?.items.length ?? 0;
      if (!current && waiting === 0) {
        return {
          kind: "empty_queue",
          tone: "info",
          icon: "queue",
          title: "キューは空です",
          detail: "曲を追加すると、BotがVCで再生します。",
        };
      }
      return {
        kind: "idle",
        tone: "info",
        icon: "play",
        title: "再生していません",
        detail: `キューに${waiting}曲あります。再生を押すと始まります。`,
      };
    }
  }
}
